import { useEffect, useState } from 'react';
import { useParams } from '@tanstack/react-router';
import { AlertCircle } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { AuthLayout } from '../components/AuthLayout';
import { buildLoginRedirectPath } from '../lib/login-redirect';
import { fetchViewSiteUrl, isValidSiteId } from '../lib/view-site';

// Opened by the Playground view page when it has no token (a reload or a
// bookmark). Gets a fresh view token with the dashboard session and sends the
// tab back to the view page. Errors stay here, so a failure never bounces
// between the two pages.
export function SiteViewRedirectPage() {
  const { siteId } = useParams({ from: '/sites/$siteId/view' });
  const { isLoading: authLoading, isAuthenticated } = useAuth();
  const validSiteId = isValidSiteId(siteId);
  const [error, setError] = useState(validSiteId ? '' : 'Invalid site ID.');

  useEffect(() => {
    if (!validSiteId || authLoading) return;
    if (!isAuthenticated) {
      const returnPath = `${window.location.pathname}${window.location.search}`;
      window.location.assign(buildLoginRedirectPath(returnPath));
      return;
    }

    let cancelled = false;
    fetchViewSiteUrl(siteId, window.location.search)
      .then((url) => {
        if (!cancelled) window.location.replace(url);
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Could not open site');
        }
      });
    return () => {
      cancelled = true;
    };
  }, [authLoading, isAuthenticated, siteId, validSiteId]);

  if (!error) {
    return (
      <AuthLayout>
        <div className="text-center text-white/60">Opening site...</div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout maxWidth="sm">
      <div className="rounded-xl border border-red-500/20 bg-red-500/10 p-6 text-center">
        <AlertCircle className="h-10 w-10 text-red-400 mx-auto mb-3" />
        <h2 className="text-lg font-semibold text-white mb-2">
          Couldn't open this site
        </h2>
        <p className="text-white/60 text-sm mb-4">{error}</p>
        <a
          href={validSiteId ? `/sites/${encodeURIComponent(siteId)}` : '/'}
          className="text-sm text-white underline"
        >
          {validSiteId ? 'Back to the site' : 'Back to the dashboard'}
        </a>
      </div>
    </AuthLayout>
  );
}
